/*!
 * Proton "Add RESET paddle cleaner" accessory attach — 2026-07-31
 *
 * Goal (Diego): the RESET™ Pickleball Paddle Cleaner ($14.99, Shopify Collective)
 * is an accessory nobody searches for on its own. Its entire job is ATTACH RATE on
 * a paddle purchase.
 *
 * TWO SURFACES, ONE ASSET:
 *   (A) PDP INLINE TILE  [PRIMARY, default ON]  — an "Add to your paddle" row injected
 *       into the buy box on paddle product pages, directly under Add to cart / Buy with
 *       Shop. This is what Diego asked for: it sits in the right-hand column beside the
 *       paddle image, reads as an accessory, and adds in one tap.
 *   (B) POST-ATC MODAL   [ON] — same treatment as the free-hat popup, fired AFTER it.
 *       Catches everyone who adds a paddle from a collection or quick-add and never
 *       saw the tile. Never double-asks: if the cleaner is already in the cart (e.g.
 *       added via the tile), the modal stays shut.
 *
 * CRITICAL — SEQUENCING WITH THE FREE HAT POPUP (paddle-hat-popup.js):
 *   Both fire on the same trigger (paddle enters cart). Two modals at once is a
 *   conversion killer, so we queue behind the hat:
 *     - never render while #proton-hat-ov is in the DOM
 *     - never render until the hat popup has been OFFERED (proton_hat_offered=1)
 *       or the hat is already in the cart, i.e. that flow has resolved
 *     - extra 900ms settle delay so the theme's own cart drawer lands first
 *     - and we POLL for the hat to clear (see reconcile). The hat's "No thanks" path
 *       fires no cart request, so there is nothing to re-trigger us — without the
 *       poll, anyone who declined the hat would never see the cleaner offer at all.
 *   Order is deliberate: FREE thing first (pure gain, builds goodwill), PAID
 *   thing second (asks for money once they're already happy).
 *
 * ANTI-NAG (mirrors the hat build):
 *   - offered at most once per cart
 *   - "No thanks" => never re-offer until the cart is emptied of paddles
 *   - if the shopper removes the cleaner themselves, we honor it (no re-add)
 *   - we NEVER auto-remove the cleaner — it's a PAID line the customer chose.
 *     (The hat is a $0 gift, so that build can tidy it. This one must not.)
 *
 * ELIGIBILITY: cart line product_type === "paddle" (cart.js exposes product_type),
 *   with a collection fallback. Deliberately NOT scoped to the free-ride collection
 *   so it keeps working after that promo ends.
 *
 * PRICE: fetched live from /products/<handle>.js so the modal can never show a
 *   stale price. Falls back to 14.99.
 *
 * Kill switch: window.ProtonCleaner.disable()   (or set PROTON_CLEANER_ENABLED=false)
 * Re-test:     window.ProtonCleaner.reset(); window.ProtonCleaner.show()
 * Deploy:      node ~/proton/scripts/_deploy-paddle-cleaner-attach.js
 */
(function () {
  "use strict";

  // (B) post-ATC modal — ON (Diego, 2026-08-13): "turn the RESET paddle cleaner
  // pop-up back on." Re-enabled after the 2026-08-07 pause.
  // The PDP inline buy-box tile (A) is and always was LIVE — it is not a popup.
  // To pause the popup again: flip this to false, then
  //   node ~/proton/scripts/_deploy-paddle-cleaner-attach.js
  // (never run the deployer with --remove: that rips the script tag out of
  //  theme.liquid and kills the PDP tile too.)
  // NOTE: the free-hat modal it used to sequence behind was killed 2026-08-06, so
  // hatFlowBlocking() no longer gates anything — this fires on the first paddle add.
  // (B) paused again 2026-08-28 (Diego: "take off this pop-up from happening on Peacocks
  // and stuff") — the cart DRAWER now shows the cleaner in its "You may also like" rail
  // (proton-upsell.js), so the popup double-prompted and covered the drawer.
  // The PDP tile (A) stays ON. To revive the popup: flip to true + redeploy.
  var MODAL_ENABLED = false;
  // 2026-09-07 (Diego: "remove this from all product pages") — kills surface (A), the
  // "ADD TO YOUR PADDLE" inline tile in the buy box. The cleaner is still offered in the
  // cart drawer rail (proton-upsell.js), so removing the tile does not lose the upsell.
  // Flip back to true + redeploy to restore. Deliberately NOT `--remove`: that rips the
  // script tag out of theme.liquid entirely.
  var TILE_ENABLED  = false;

  var ADDON_PROP    = "_proton_addon";
  var CLEANER_VID   = 55967795937572;          // RESET™ Pickleball Paddle Cleaner
  var CLEANER_PID   = 10753234075940;
  var CLEANER_HANDLE= "reset-pickleball-paddle-cleaner";
  var CLEANER_IMG   = "https://cdn.shopify.com/s/files/1/0717/0372/4324/files/paddleresetspray-large-main.jpg?v=1785535211";
  var FALLBACK_PRICE= 14.99;

  var PADDLE_COLLECTION = "pickleball-paddles";  // fallback eligibility source

  var LS_OFFERED   = "proton_cleaner_offered";
  var LS_DISMISSED = "proton_cleaner_dismissed";

  var STATE = { busy: false, open: false, timer: null, price: null, paddleIds: null, hatWaits: 0 };

  // How long we'll keep waiting for the free-hat modal to clear before giving up.
  var HAT_WAIT_MS = 700, HAT_WAIT_MAX = 40;   // ~28s ceiling

  function log() { try { console.info.apply(console, ["[paddle-cleaner]"].concat([].slice.call(arguments))); } catch (e) {} }
  function lsGet(k) { try { return localStorage.getItem(k); } catch (e) { return null; } }
  function lsSet(k, v) { try { localStorage.setItem(k, v); } catch (e) {} }

  function money(n) {
    var v = (typeof n === "number" && isFinite(n)) ? n : FALLBACK_PRICE;
    return "$" + v.toFixed(2);
  }

  function inactiveContext() {
    try {
      if (window.ProtonCleaner && window.ProtonCleaner._disabled) return true;
      if (window.PROTON_CLEANER_ENABLED === false) return true;
      if (window.Shopify && window.Shopify.designMode) return true;
      var p = location.pathname || "";
      if (p.indexOf("/checkout") === 0 || p.indexOf("/checkouts") === 0) return true;
      // Don't upsell the cleaner while the shopper is reading the cleaner's own page.
      if (p.indexOf("/products/" + CLEANER_HANDLE) === 0) return true;
    } catch (e) {}
    return false;
  }

  // The free-hat modal owns the screen first. Stand down until it's resolved.
  function hatFlowBlocking(cart) {
    try {
      if (document.getElementById("proton-hat-ov")) return true;      // hat modal on screen
      if (window.ProtonHat && window.ProtonHat._disabled) return false; // hat build off -> we're free
      if (!window.ProtonHat || !window.ProtonHat._booted) return false; // hat build absent -> we're free
      if (lsGet("proton_hat_offered") === "1") return false;           // hat already offered
      if (lsGet("proton_hat_dismissed") === "1") return false;         // hat already declined
      if (cart && Array.isArray(cart.items)) {                         // hat already in cart
        for (var i = 0; i < cart.items.length; i++) {
          var pr = cart.items[i].properties || {};
          if (String(pr._proton_gift) === "1") return false;
        }
      }
      return true;  // hat build is live but hasn't had its turn yet
    } catch (e) { return false; }
  }

  function fetchJSON(url, opts) {
    return fetch(url, Object.assign({ credentials: "same-origin", headers: { Accept: "application/json" } }, opts || {}))
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
  }

  function getCart() { return fetchJSON("/cart.js"); }

  function loadPrice() {
    if (STATE.price != null) return Promise.resolve(STATE.price);
    return fetchJSON("/products/" + CLEANER_HANDLE + ".js").then(function (p) {
      var cents = p && p.variants && p.variants[0] && p.variants[0].price;
      STATE.price = (typeof cents === "number") ? cents / 100 : FALLBACK_PRICE;
      return STATE.price;
    }).catch(function () { STATE.price = FALLBACK_PRICE; return STATE.price; });
  }

  function loadPaddleIds() {
    if (STATE.paddleIds) return Promise.resolve(STATE.paddleIds);
    return fetchJSON("/collections/" + PADDLE_COLLECTION + "/products.json?limit=250")
      .then(function (j) {
        STATE.paddleIds = (j && Array.isArray(j.products))
          ? j.products.map(function (p) { return Number(p.id); }).filter(Boolean) : [];
        return STATE.paddleIds;
      }).catch(function () { STATE.paddleIds = []; return STATE.paddleIds; });
  }

  function hasPaddle(cart, paddleIds) {
    if (!cart || !Array.isArray(cart.items)) return false;
    return cart.items.some(function (it) {
      if (isNoUpsell(it.handle)) return false;   // pre-orders never trigger the offer
      var t = String(it.product_type || "").toLowerCase();
      if (t === "paddle") return true;
      return paddleIds.indexOf(Number(it.product_id)) !== -1;
    });
  }
  function cleanerInCart(cart) {
    if (!cart || !Array.isArray(cart.items)) return false;
    return cart.items.some(function (it) {
      return Number(it.variant_id) === CLEANER_VID || Number(it.product_id) === CLEANER_PID;
    });
  }

  function addCleaner() {
    var body = { items: [{ id: CLEANER_VID, quantity: 1, properties: {} }] };
    body.items[0].properties[ADDON_PROP] = "1";
    return fetch("/cart/add.js", {
      method: "POST", credentials: "same-origin",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(body)
    }).then(function (r) { return r.ok ? r.json() : null; }).catch(function () { return null; });
  }

  function notifyThemeCartChanged() {
    try {
      document.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
      document.dispatchEvent(new CustomEvent("cart:updated", { bubbles: true }));
      if (window.PubSub && window.PubSub.publish) window.PubSub.publish("cart-update");
    } catch (e) {}
  }

  /* ---------------- modal UI (glass + gradient + glow, mobile-first) ---------------- */
  var STYLE_ID = "proton-cleaner-style";
  function injectStyle() {
    if (document.getElementById(STYLE_ID)) return;
    var s = document.createElement("style");
    s.id = STYLE_ID;
    s.textContent =
    ".pc-ov,.pc-ov *{box-sizing:border-box}"+
    ".pc-ov{position:fixed;inset:0;z-index:2147482900;display:flex;align-items:flex-end;justify-content:center;background:rgba(9,20,40,.55);-webkit-backdrop-filter:blur(6px);backdrop-filter:blur(6px);opacity:0;transition:opacity .22s;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif}"+
    ".pc-ov.pc-in{opacity:1}"+
    "@media(min-width:640px){.pc-ov{align-items:center}}"+
    ".pc-card{position:relative;width:100%;max-width:460px;max-height:92vh;overflow:auto;background:linear-gradient(180deg,#eaf3ff 0%,#f7fbff 34%,#ffffff 72%);border-radius:22px 22px 0 0;border:1px solid rgba(21,133,199,.18);box-shadow:0 -18px 60px rgba(11,31,58,.34);padding:20px 18px 22px;transform:translateY(24px);transition:transform .26s;color:#0b1f3a}"+
    "@media(min-width:640px){.pc-card{border-radius:24px}}"+
    ".pc-ov.pc-in .pc-card{transform:translateY(0)}"+
    ".pc-x{position:absolute;top:12px;right:14px;width:34px;height:34px;border:none;border-radius:50%;background:rgba(255,255,255,.7);color:#5b6a82;font-size:20px;font-weight:700;cursor:pointer;box-shadow:0 4px 12px rgba(11,31,58,.12)}"+
    ".pc-badge{display:inline-block;background:linear-gradient(135deg,#1585c7,#0b57a3);color:#fff;font-size:11px;font-weight:800;letter-spacing:.08em;text-transform:uppercase;padding:6px 13px;border-radius:999px;box-shadow:0 8px 20px rgba(21,133,199,.32)}"+
    ".pc-h{font-size:22px;line-height:1.12;font-weight:900;letter-spacing:-.015em;margin:11px 0 4px}"+
    ".pc-h b{color:#1585c7}"+
    ".pc-sub{font-size:13.5px;color:#5b6a82;font-weight:600;margin:0 0 15px}"+
    ".pc-row{display:flex;gap:13px;align-items:center;background:rgba(255,255,255,.78);border:2px solid rgba(21,133,199,.16);border-radius:16px;padding:11px}"+
    ".pc-imw{flex:0 0 84px;width:84px;border-radius:12px;overflow:hidden;background:linear-gradient(165deg,#f2f8ff,#fff)}"+
    ".pc-imw img{width:100%;aspect-ratio:1;object-fit:contain;display:block}"+
    ".pc-info{flex:1 1 auto;min-width:0;text-align:left}"+
    ".pc-nm{font-weight:900;font-size:14px;line-height:1.2}"+
    ".pc-bl{margin:6px 0 0;padding:0;list-style:none;font-size:11.5px;font-weight:600;color:#5b6a82;line-height:1.5}"+
    ".pc-bl li:before{content:'✓';color:#12a86b;font-weight:900;margin-right:5px}"+
    ".pc-pr{font-weight:900;font-size:16px;color:#0b1f3a;margin-top:7px}"+
    ".pc-cta{margin-top:16px;width:100%;padding:15px;border:none;border-radius:14px;background:linear-gradient(135deg,#1585c7,#0b57a3);color:#fff;font-weight:800;font-size:15.5px;cursor:pointer;box-shadow:0 12px 26px rgba(21,133,199,.4);letter-spacing:.01em;transition:filter .15s,transform .1s}"+
    ".pc-cta:hover{filter:brightness(1.08)}.pc-cta:active{transform:scale(.99)}"+
    ".pc-cta[disabled]{opacity:.7;cursor:default}"+
    ".pc-no{display:block;margin:11px auto 0;background:none;border:none;color:#8a94a6;font-size:12.5px;font-weight:700;cursor:pointer;text-decoration:underline}"+
    ".pc-note{text-align:center;font-size:11.5px;color:#9aa3b2;font-weight:600;margin:9px 0 0}";
    document.head.appendChild(s);
  }

  function closeModal() {
    var ov = document.getElementById("proton-cleaner-ov");
    if (!ov) { STATE.open = false; return; }
    ov.classList.remove("pc-in");
    setTimeout(function () { if (ov && ov.parentNode) ov.parentNode.removeChild(ov); }, 260);
    STATE.open = false;
  }

  function openModal() {
    if (STATE.open || document.getElementById("proton-cleaner-ov")) return;
    injectStyle();
    STATE.open = true;
    var price = money(STATE.price);
    var ov = document.createElement("div");
    ov.className = "pc-ov";
    ov.id = "proton-cleaner-ov";
    ov.innerHTML =
      "<div class='pc-card' role='dialog' aria-modal='true' aria-label='Add RESET paddle cleaner'>" +
        "<button class='pc-x' aria-label='Close'>×</button>" +
        "<span class='pc-badge'>Complete your setup</span>" +
        "<div class='pc-h'>Keep that <b>fresh-paddle grit</b></div>" +
        "<p class='pc-sub'>Ball fibers and court dust kill your spin within a few sessions. One spray brings the texture back.</p>" +
        "<div class='pc-row'>" +
          "<div class='pc-imw'><img src='" + CLEANER_IMG + "' alt='RESET Pickleball Paddle Cleaner' loading='lazy'></div>" +
          "<div class='pc-info'>" +
            "<div class='pc-nm'>RESET&trade; Paddle Cleaner</div>" +
            "<ul class='pc-bl'><li>Restores spin &amp; tack</li><li>Safe on carbon fiber</li><li>Travel size, fits your bag</li></ul>" +
            "<div class='pc-pr'>" + price + "</div>" +
          "</div>" +
        "</div>" +
        "<button class='pc-cta'>Add to my order &mdash; " + price + "</button>" +
        "<button class='pc-no'>No thanks</button>" +
        // NOTE: deliberately makes NO shipping claim. This is a Shopify Collective
        // dropship from RESET Pickleball and can ship separately from the paddle —
        // promising "ships with your order" would be a CS ticket waiting to happen.
        "<p class='pc-note'>Non-toxic &amp; alcohol-free &middot; 4 oz travel bottle</p>" +
      "</div>";
    document.body.appendChild(ov);
    requestAnimationFrame(function () { ov.classList.add("pc-in"); });
    lsSet(LS_OFFERED, "1");

    ov.addEventListener("click", function (e) {
      if (e.target === ov) { dismiss(); return; }
      var c = e.target.closest;
      if (c && (e.target.closest(".pc-x") || e.target.closest(".pc-no"))) { dismiss(); return; }
      var cta = c && e.target.closest(".pc-cta");
      if (cta) {
        cta.setAttribute("disabled", "disabled");
        cta.textContent = "Adding…";
        addCleaner().then(function () {
          notifyThemeCartChanged();
          cta.textContent = "Added! ✓";
          setTimeout(closeModal, 650);
        });
      }
    });
  }

  function dismiss() { lsSet(LS_DISMISSED, "1"); closeModal(); }

  /* ================= (A) PDP INLINE TILE — the primary ask =================
   * Injected into the Dawn buy box on paddle PDPs, immediately after the
   * <product-form> element (i.e. below Add to cart / Buy with Shop / More
   * payment options, above Specifications). Deliberately NOT above Add to
   * cart: we never want an accessory competing with the $180 primary CTA.
   *
   * Dawn re-renders the product form on variant change, which would blow the
   * tile away — so a MutationObserver re-injects it.
   * ======================================================================== */
  var TILE_ID = "proton-cleaner-tile";
  var TILE_STYLE_ID = "proton-cleaner-tile-style";
  var PDP = { isPaddle: null, observer: null };

  function injectTileStyle() {
    if (document.getElementById(TILE_STYLE_ID)) return;
    var s = document.createElement("style");
    s.id = TILE_STYLE_ID;
    s.textContent =
    "#" + TILE_ID + ",#" + TILE_ID + " *{box-sizing:border-box}"+
    "#" + TILE_ID + "{margin:16px 0 4px;border-radius:16px;padding:12px;background:linear-gradient(158deg,rgba(234,243,255,.95) 0%,rgba(247,251,255,.9) 45%,rgba(255,255,255,.92) 100%);border:1px solid rgba(21,133,199,.22);box-shadow:0 8px 24px rgba(11,31,58,.09),inset 0 1px 0 rgba(255,255,255,.7);-webkit-backdrop-filter:blur(8px);backdrop-filter:blur(8px);font-family:inherit;color:#0b1f3a;transition:border-color .18s,box-shadow .18s}"+
    "#" + TILE_ID + ".pct-on{border-color:rgba(18,168,107,.55);box-shadow:0 10px 28px rgba(18,168,107,.18),inset 0 1px 0 rgba(255,255,255,.7)}"+
    "#" + TILE_ID + " .pct-lbl{display:block;font-size:10.5px;font-weight:800;letter-spacing:.1em;text-transform:uppercase;color:#1585c7;margin:0 0 9px}"+
    "#" + TILE_ID + " .pct-row{display:flex;align-items:center;gap:12px}"+
    "#" + TILE_ID + " .pct-im{flex:0 0 58px;width:58px;height:58px;border-radius:11px;overflow:hidden;background:linear-gradient(165deg,#f2f8ff,#fff);border:1px solid rgba(21,133,199,.12)}"+
    "#" + TILE_ID + " .pct-im img{width:100%;height:100%;object-fit:contain;display:block}"+
    "#" + TILE_ID + " .pct-tx{flex:1 1 auto;min-width:0}"+
    "#" + TILE_ID + " .pct-nm{font-size:13.5px;font-weight:800;line-height:1.25;margin:0}"+
    "#" + TILE_ID + " .pct-nm a{color:inherit;text-decoration:none}"+
    "#" + TILE_ID + " .pct-nm a:hover{text-decoration:underline}"+
    "#" + TILE_ID + " .pct-d{font-size:11.5px;font-weight:600;color:#5b6a82;margin:3px 0 0;line-height:1.35}"+
    "#" + TILE_ID + " .pct-p{font-size:13.5px;font-weight:900;margin:5px 0 0}"+
    "#" + TILE_ID + " .pct-btn{flex:0 0 auto;border:none;cursor:pointer;border-radius:999px;padding:10px 17px;font-size:12.5px;font-weight:800;letter-spacing:.01em;color:#fff;background:linear-gradient(135deg,#1585c7,#0b57a3);box-shadow:0 6px 16px rgba(21,133,199,.36);transition:filter .15s,transform .1s,background .2s}"+
    "#" + TILE_ID + " .pct-btn:hover{filter:brightness(1.08)}"+
    "#" + TILE_ID + " .pct-btn:active{transform:scale(.97)}"+
    "#" + TILE_ID + " .pct-btn[disabled]{opacity:.65;cursor:default}"+
    "#" + TILE_ID + ".pct-on .pct-btn{background:linear-gradient(135deg,#12a86b,#0b7a53);box-shadow:0 6px 16px rgba(18,168,107,.34)}"+
    "@media(max-width:480px){#" + TILE_ID + " .pct-btn{padding:9px 14px;font-size:12px}}";
    document.head.appendChild(s);
  }

  // Live product types in this catalog: accessory, bag, Bat, Batting Gloves, Bundle,
  // Free Gift, Hat, Hats, paddle, "paddle bundle", Shirt.
  // We want "paddle" AND "paddle bundle" (a bundle buyer wants paddle care too),
  // but never Bat / bag / apparel — hence prefix match, not equality.
  function isPaddleType(t) {
    return String(t || "").trim().toLowerCase().indexOf("paddle") === 0;
  }

  function currentHandle() {
    var m = (location.pathname || "").match(/\/products\/([^/?#]+)/);
    return m ? m[1] : null;
  }

  // ── NO-UPSELL PRODUCTS (Diego, 2026-08-07) ────────────────────────────────────
  // "make sure all bundles and deals and add to cart and everything is off for the
  //  coyote. We [don't] want bundles to work with the coyote, or % offs etc."
  //
  // The Project Coyote pre-order is product_type "paddle", so it matched isPaddleType()
  // and the buy-box tile rendered on its PDP — an accessory cross-sell stapled to a
  // launch pre-order. Gate BOTH surfaces (tile and post-ATC modal) on the handle.
  //
  // Handle list, not the `no-discounts` product tag: cart.js and ShopifyAnalytics.meta
  // expose the handle on every surface we run on, and neither exposes tags. The
  // Shopify-side lock keys off the tag; this is the storefront half of the same rule.
  //
  // 2026-08-24: the other seven Coyote surfaces were tagged `no-discounts` when Diego
  // capped everything Coyote at 10%, so they are listed here too. Only the first entry
  // is load-bearing today — the tile and the post-ATC modal are both gated on
  // isPaddleType(), and of these products only the pre-order is product_type "paddle"
  // (the bundles are "Bundle", the tee "apparel", the hat "Hats", the bag "bag"). The
  // rest are belt-and-braces against a plausible edit: these ARE paddle bundles, and the
  // day someone retypes one to "paddle" for a collection rule, an accessory cross-sell
  // would appear inside a launch pre-order. A string in a list is cheaper than that.
  var NO_UPSELL_HANDLES = [
    "project-coyote-preorder", "project-coyote",
    "coyote-bundle-tee", "coyote-bundle-hat", "coyote-bundle-tee-hat", "coyote-bundle-full",
    "coyote-kit",
    "project-coyote-tee", "project-coyote-hat", "atomic-pickleball-backpack",
  ];
  function isNoUpsell(h) {
    return !!h && NO_UPSELL_HANDLES.indexOf(String(h)) !== -1;
  }

  // Is the PDP we're on a paddle?
  //
  // PRIMARY: window.ShopifyAnalytics.meta.product.type — Shopify prints this inline on
  //   EVERY product page natively (verified live: type:"paddle"). Zero network, zero
  //   custom Liquid, immune to rate limiting, and it's there before our deferred script
  //   runs. Don't replace this with a fetch.
  //
  // Why not a network lookup: /products/<handle>.js can return a 429 HTML error page
  //   under load, which silently killed the tile in testing. It stays only as a last
  //   resort, and it must never be the sole source of truth.
  function isPaddlePDP() {
    if (PDP.isPaddle !== null) return Promise.resolve(PDP.isPaddle);
    var h = currentHandle();
    if (!h || h === CLEANER_HANDLE) { PDP.isPaddle = false; return Promise.resolve(false); }
    if (isNoUpsell(h)) {
      log("no-upsell product (" + h + ") — tile suppressed");
      PDP.isPaddle = false; return Promise.resolve(false);
    }

    var t = null;
    try {
      var m = window.ShopifyAnalytics && window.ShopifyAnalytics.meta;
      if (m && m.product && typeof m.product.type === "string") t = m.product.type;
    } catch (e) {}
    if (t === null) { try { if (window.PROTON_PRODUCT && window.PROTON_PRODUCT.type) t = window.PROTON_PRODUCT.type; } catch (e) {} }

    if (typeof t === "string") {
      PDP.isPaddle = isPaddleType(t);
      log("product type:", t, "-> paddle:", PDP.isPaddle);
      return Promise.resolve(PDP.isPaddle);
    }

    return fetchJSON("/products/" + h + ".js").then(function (p) {
      PDP.isPaddle = !!p && isPaddleType(p.type || p.product_type);
      log("product type via fetch fallback -> paddle:", PDP.isPaddle);
      return PDP.isPaddle;
    }).catch(function () { PDP.isPaddle = false; return false; });
  }

  function tileMarkup(added) {
    var price = money(STATE.price);
    return "<span class='pct-lbl'>Add to your paddle</span>" +
      "<div class='pct-row'>" +
        "<div class='pct-im'><img src='" + CLEANER_IMG + "' alt='RESET Pickleball Paddle Cleaner' loading='lazy'></div>" +
        "<div class='pct-tx'>" +
          "<p class='pct-nm'><a href='/products/" + CLEANER_HANDLE + "'>RESET&trade; Paddle Cleaner</a></p>" +
          "<p class='pct-d'>Restores spin, grit &amp; tack. Safe on carbon fiber.</p>" +
          "<p class='pct-p'>" + price + "</p>" +
        "</div>" +
        "<button type='button' class='pct-btn'>" + (added ? "Added ✓" : "+ Add") + "</button>" +
      "</div>";
  }

  function renderTile() {
    if (!TILE_ENABLED) return;
    if (document.getElementById(TILE_ID)) return;
    var form = document.querySelector("product-form") ||
               (document.querySelector(".product-form__buttons") || {}).parentElement;
    if (!form || !form.parentElement) return;
    injectTileStyle();

    var el = document.createElement("div");
    el.id = TILE_ID;
    el.innerHTML = tileMarkup(false);
    form.parentElement.insertBefore(el, form.nextSibling);

    el.addEventListener("click", function (e) {
      var btn = e.target.closest && e.target.closest(".pct-btn");
      if (!btn || btn.hasAttribute("disabled")) return;
      var isAdded = el.classList.contains("pct-on");
      btn.setAttribute("disabled", "disabled");
      btn.textContent = isAdded ? "Removing…" : "Adding…";
      (isAdded ? removeCleanerLines() : addCleaner()).then(function () {
        notifyThemeCartChanged();
        return syncTile();
      });
    });
    syncTile();
  }

  function removeCleanerLines() {
    return getCart().then(function (cart) {
      var lines = (cart && cart.items || []).filter(function (it) {
        return Number(it.variant_id) === CLEANER_VID || Number(it.product_id) === CLEANER_PID;
      });
      return lines.reduce(function (p, ln) {
        return p.then(function () {
          return fetch("/cart/change.js", {
            method: "POST", credentials: "same-origin",
            headers: { "Content-Type": "application/json", Accept: "application/json" },
            body: JSON.stringify({ id: ln.key, quantity: 0 })
          }).catch(function () {});
        });
      }, Promise.resolve());
    });
  }

  function syncTile() {
    var el = document.getElementById(TILE_ID);
    if (!el) return Promise.resolve();
    return getCart().then(function (cart) {
      // A failed/rate-limited /cart.js returns null. Do NOT treat that as "not in cart"
      // — that would flip a genuine "Added ✓" back to "+ Add" and invite a double-add.
      // Leave the tile exactly as it is and just re-enable the button.
      var btn = el.querySelector(".pct-btn");
      if (!cart || !Array.isArray(cart.items)) {
        if (btn) btn.removeAttribute("disabled");
        return;
      }
      var added = cleanerInCart(cart);
      el.classList.toggle("pct-on", added);
      if (btn) { btn.textContent = added ? "Added ✓" : "+ Add"; btn.removeAttribute("disabled"); }
    }).catch(function () {
      var b = el.querySelector(".pct-btn"); if (b) b.removeAttribute("disabled");
    });
  }

  function bootPDP(attempt) {
    if (inactiveContext()) return;
    attempt = attempt || 1;
    // If ShopifyAnalytics.meta hasn't populated yet, don't cache a false negative —
    // clear the memo and retry a couple of times before giving up.
    var metaReady = false;
    try {
      var mm = window.ShopifyAnalytics && window.ShopifyAnalytics.meta;
      metaReady = !!(mm && mm.product && mm.product.type);
    } catch (e) {}
    if (!metaReady && attempt <= 3 && /\/products\//.test(location.pathname)) {
      PDP.isPaddle = null;
      setTimeout(function () { bootPDP(attempt + 1); }, 700);
      if (attempt < 3) return;
    }
    isPaddlePDP().then(function (yes) {
      if (!yes) return;
      loadPrice().then(function () {
        renderTile();
        // Dawn swaps the product form on variant change — re-inject if it vanishes.
        if (!PDP.observer) {
          var host = document.querySelector(".product__info-container") || document.body;
          PDP.observer = new MutationObserver(function () {
            if (TILE_ENABLED && !document.getElementById(TILE_ID)) renderTile();
          });
          try { PDP.observer.observe(host, { childList: true, subtree: true }); } catch (e) {}
        }
      });
    });
  }

  /* ---------------- reconcile ---------------- */
  function reconcile() {
    if (STATE.busy || inactiveContext()) return;
    STATE.busy = true;
    Promise.all([loadPaddleIds(), loadPrice(), getCart()]).then(function (r) {
      var paddleIds = r[0], cart = r[2];
      if (!cart) return;

      // No paddle in cart -> reset flags for a clean slate. NEVER remove the
      // cleaner itself: it's a paid line the shopper chose to keep.
      if (!hasPaddle(cart, paddleIds)) {
        lsSet(LS_OFFERED, "0"); lsSet(LS_DISMISSED, "0");
        STATE.hatWaits = 0;
        if (STATE.open) closeModal();
        return;
      }
      if (cleanerInCart(cart)) { if (STATE.open) closeModal(); return; }
      if (!MODAL_ENABLED) return;                         // PDP tile is the primary ask
      if (lsGet(LS_DISMISSED) === "1") return;
      if (lsGet(LS_OFFERED) === "1") return;

      // Wait our turn behind the free-hat modal, then take the floor.
      //
      // We must POLL here, not just return. The "No thanks" path on the hat modal
      // fires no cart request at all, so there is no /cart/add.js to re-trigger us —
      // if we simply bailed, the cleaner offer would never appear for anyone who
      // declined the hat. The retry also gives a natural beat between the two modals
      // so they never cross-fade into each other.
      if (hatFlowBlocking(cart)) {
        if (STATE.hatWaits < HAT_WAIT_MAX) {
          STATE.hatWaits++;
          setTimeout(reconcile, HAT_WAIT_MS);
        } else {
          log("gave up waiting on the free-hat flow");
        }
        return;
      }
      STATE.hatWaits = 0;

      openModal();
    }).catch(function (e) { log("reconcile error", e && e.message); })
      .then(function () { STATE.busy = false; });
  }

  // 900ms settle: let the theme's cart drawer + the hat modal land first.
  function schedule() { if (STATE.timer) clearTimeout(STATE.timer); STATE.timer = setTimeout(reconcile, 900); }

  var CART_RE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/i;
  function hookFetch() {
    if (!window.fetch || window.fetch.__pcHooked) return;
    var orig = window.fetch;
    var wrapped = function (input) {
      var url = (typeof input === "string") ? input : (input && input.url) || "";
      var pr = orig.apply(this, arguments);
      try { if (CART_RE.test(url)) pr.then(function () { schedule(); }).catch(function () {}); } catch (e) {}
      return pr;
    };
    wrapped.__pcHooked = true;
    window.fetch = wrapped;
  }
  function hookXHR() {
    try {
      var open = XMLHttpRequest.prototype.open;
      if (open.__pcHooked) return;
      var wrapped = function (method, url) {
        try { if (CART_RE.test(url || "")) this.addEventListener("load", function () { schedule(); }); } catch (e) {}
        return open.apply(this, arguments);
      };
      wrapped.__pcHooked = true;
      XMLHttpRequest.prototype.open = wrapped;
    } catch (e) {}
  }

  function init() {
    if (window.ProtonCleaner && window.ProtonCleaner._booted) return;
    hookFetch(); hookXHR();
    schedule();
    bootPDP();
    ["cart:updated", "cart:refresh", "cart:change"].forEach(function (ev) {
      document.addEventListener(ev, schedule);
      document.addEventListener(ev, syncTile);
    });
    try {
      window.ProtonCleaner = Object.assign(window.ProtonCleaner || {}, {
        _version: "2026-07-31", _booted: true, _disabled: false,
        _modalEnabled: MODAL_ENABLED,
        show: openModal, reconcile: reconcile, tile: renderTile, sync: syncTile,
        disable: function () {
          window.ProtonCleaner._disabled = true;
          if (STATE.open) closeModal();
          var t = document.getElementById(TILE_ID); if (t && t.parentNode) t.parentNode.removeChild(t);
          if (PDP.observer) { try { PDP.observer.disconnect(); } catch (e) {} PDP.observer = null; }
        },
        reset: function () { lsSet(LS_OFFERED, "0"); lsSet(LS_DISMISSED, "0"); schedule(); }
      });
    } catch (e) {}
    log("active — cleaner variant", CLEANER_VID, "| modal:", MODAL_ENABLED ? "on" : "off (PDP tile is primary)");
  }

  if (document.readyState === "loading") { document.addEventListener("DOMContentLoaded", init); } else { init(); }
})();
